import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, Signal, ViewChild, WritableSignal, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of } from 'rxjs';
import { catchError, delay, finalize, map, mergeMap, shareReplay, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { LocalizationService, LocalizationSettings  } from 'src/app/shared/localization/localization.service';
import { WebUserRight } from 'src/app/shared/models';
import { UtilsService } from 'src/app/shared/utils.service';
import { CasesComponent } from '../cases/cases.component';
import { ChangeStatusComponent } from '../change-status/change-status.component';
import { ContractsService } from '../contracts.service';
import { ContractStatusChangeActionTypeEnum, DebtorTypeEnum, ICaseContractStatusHistory, IChangedContract, IContractClaimStatusSummary, IContractClaimTypeSummary, IContractSummary, IExternalContractStatus, IFilterContracts, IUpdatedContractsMultipleStatuses, ScoreColorEnum } from '../models';
import { NotesComponent } from '../notes/notes.component';
import { SortEvent } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { Popover, PopoverModule } from 'primeng/popover';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { ContractTypeStatusStatisticsComponent } from '../contract-type-status-statistics/contract-type-status-statistics.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { SelectModule } from 'primeng/select';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.scss'],
  imports: [CommonModule, TranslateModule, TableModule, MatTooltipModule, FormsModule, ContractTypeStatusStatisticsComponent,
    InputNumberModule, CustomSelectComponent, SelectModule, PopoverModule, DatePickerModule]
})
export class ContractsComponent implements OnInit, OnDestroy {

  @ViewChild('externalStatus') public externalStatus: Popover;
  @ViewChild('contractsTable') contractsTable: Table;
  subscriptions: Subscription[] = [];
  keyValueClaimTypes: KeyValue<string, string>[];
  claimTypes: IContractClaimTypeSummary[];
  statuses: IContractClaimStatusSummary[] = [];

  filter: IFilterContracts = { customerNumber: null };
  debtorTypes$: Observable<KeyValue<string, string>[]>;
  scoreColors$: Observable<KeyValue<string, string>[]>;
  private availableDebtorTypes = [DebtorTypeEnum.Person, DebtorTypeEnum.Company];
  private availableScoreColors = [ScoreColorEnum.Red, ScoreColorEnum.Yellow, ScoreColorEnum.Green, ScoreColorEnum.NotRated];

  currentPage = 1;
  numberOfItemsPerPage = 25;
  totalNumberOfItems: number;
  indexOfFirstTableElement = 0;

  private contractSummarySubject = new BehaviorSubject<IContractSummary[]>([]);
  private originalContracts: IContractSummary[] = [];
  list: Signal<IContractSummary[]> = toSignal(this.contractSummarySubject.asObservable(), { initialValue: [] });

  originalList$: Observable<IContractSummary[]>;

  private unsubscribe$ = new Subject();

  contractClosingTypeChanges$ = new BehaviorSubject<IChangedContract>(null);

  isLoading = false;
  searchTerm: string;

  format$ = this.localizationService.settings$;
  customerNumber$: Observable<number> = this.application.customerNumber$;
  externalStatusData$: Observable<IExternalContractStatus>;

  createClosingOrder = ContractStatusChangeActionTypeEnum.CreateClosingOrder;
  finishClosingOrder = ContractStatusChangeActionTypeEnum.FinishClosingOrder;
  isEnglish = toSignal(this.translate.onLangChange.pipe(
      map(e => e.lang.toLocaleLowerCase()),
      startWith(this.translate.currentLang.toLocaleLowerCase()),
      map(lang => lang === 'en')
    ));
  canViewScore$: Observable<boolean> = this.application.userHasRight(WebUserRight.ViewScore).pipe(shareReplay());
  canEditContract$: Observable<boolean> = this.application.userHasRight(WebUserRight.EditContractAdmin).pipe(shareReplay());

  selectedStatus: IContractClaimStatusSummary;
  refreshStatistics = 0;
  numberOfSelectedContracts = signal(0);

  sortEvent: SortEvent;

  DEFAULT_CLOSING_TYPE = 1;

  localization = inject(LocalizationService);
  locale: LocalizationSettings = {} as LocalizationSettings;

  popupCounter = 0; 
  position : {x : number, y : number} = {x:0 , y:0};
  isMouseOverTrigger : WritableSignal<boolean> = signal(false);
  isMouseOverPopup : WritableSignal<boolean> = signal(false);

  hoverContract$: Observable<ICaseContractStatusHistory[]>;
  private contractStatusHistoryCache = new Map<number, Observable<ICaseContractStatusHistory[]>>();
  #changeDetectorRef = inject(ChangeDetectorRef);

  constructor(private service: ContractsService,
    private translate: TranslateService,
    private localizationService: LocalizationService,
    private messageToUi: MessageToUiService,
    private utils: UtilsService,
    private application: AppService,
    private matDialog: MatDialog) { }

  ngOnInit(): void {
    this.localization.settings$.subscribe(_ => { this.locale = _; });
    this.debtorTypes$ = this.translate.onLangChange.pipe(
      startWith(null),
      map(() =>
        this.availableDebtorTypes.map(x => ({ key: x, value: this.translate.instant(`Debtors.${x}`) } as KeyValue<string, string>)
        )));

    this.scoreColors$ = this.translate.onLangChange.pipe(
      startWith(null),
      map(() =>
        this.availableScoreColors.map(x => ({ key: x, value: this.translate.instant(`Contracts.ScoreColor${x}`) } as KeyValue<string, string>)
        )));

    this.getAllStatuses();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(x => x.unsubscribe());
    this.unsubscribe$.next(null);
  }

  customSort(event: SortEvent) {
    if(event.field == this.sortEvent?.field && event.order == this.sortEvent?.order) return;

    const newSortField = structuredClone(event.field);
    const resetSorting = event.order > 0 && this.sortEvent?.order === -1 && this.sortEvent?.field === newSortField;

    this.sortEvent = event;

    if (resetSorting) {
      this.contractsTable.clear();
      this.contractSummarySubject.next(this.originalContracts);
      this.sortEvent = null;

      return;
    }

    const sorted = [...this.originalContracts].sort((a, b) => this.sortTableData(a[event.field], b[event.field], event.order));

    this.contractSummarySubject.next(sorted);
  }

  sortTableData(value1, value2, order) {
      let result = null;
      
      if (value1 == null && value2 != null) { result = -1; }
      else if (value1 != null && value2 == null) { result = 1; }
      else if (value1 == null && value2 == null) { result = 0; }
      else if (typeof value1 === 'string' && typeof value2 === 'string') { result = value1.localeCompare(value2); }
      else { result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0; }

      return order * result;
  }

  getAllStatuses() {
    this.subscriptions.push(this.service.getSelectedClaimTypesForUser().subscribe(data => {
      this.claimTypes = data;
      this.keyValueClaimTypes = data.map(x => ({ key: x.claimTypeCode, value: `${x.claimTypeCode} ${x.claimTypeDescription}` } as KeyValue<string, string>));
      this.filter.contractType = this.claimTypes.find(p => p.isDefault == true)?.claimTypeCode;
      this.setStatusesForClaimType(this.filter.contractType);
    }));
  }

  setStatusesForClaimType(claimTypeCode: string) {
    const selectedClaimType = this.claimTypes?.find(p => p.claimTypeCode == claimTypeCode);
    if (selectedClaimType) {
      this.statuses = selectedClaimType.statuses;
    }
    else {
      this.statuses = [];
    }
  }

  getClassForWorkListItem(item: IContractClaimStatusSummary): string {
    if (item.workList === true) { return 'work-list'; }
    return '';
  }

  public getPageReportTemplate(): string {
    if (this.totalNumberOfItems) {
      return this.translate.instant('General.PageReportTemplate', {
        from: this.indexOfFirstTableElement + 1,
        to: this.indexOfFirstTableElement + this.numberOfItemsPerPage > this.totalNumberOfItems ? this.totalNumberOfItems : this.indexOfFirstTableElement + this.numberOfItemsPerPage,
        totalCount: this.totalNumberOfItems
      });
    }
    return '';
  }

  onSubmit() {
    this.searchTerm = '';
    this.isLoading = true;

    this.refreshStatistics++;

    this.originalList$ = this.application.customerNumber$
      .pipe(mergeMap(newCustomerNr => {
        this.filter.customerNumber = newCustomerNr;
        this.filter.statusName = this.selectedStatus?.name;
        return this.service.getContracts(this.filter).pipe(
          map(contracts =>
            contracts.map(contract => ({
              ...contract,
              numericScore: this.getNumericScore(contract.score)
            }))
          ),
          catchError(() => {
            this.messageToUi.genericError();
            return of([] as IContractSummary[]);
          }),
          tap(_ => this.totalNumberOfItems = _?.length),
          finalize(() => {
            this.isLoading = false;
          }));
      }),
        shareReplay(1));

    combineLatest([this.originalList$, this.contractClosingTypeChanges$])
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(([list, changedContract]) => {
      const newList = [...list];

      if (changedContract) {
        const indexToUpdate = newList.findIndex(c => c.id == Number(changedContract.contractStatusId));
        newList[indexToUpdate].contractClosingTypeId = Number(changedContract.closingTypeId);
      }

      this.contractSummarySubject.next(newList);
      this.originalContracts = newList; 
    });

    this.contractStatusHistoryCache.clear();
  }

  onFilter() {
    if (!this.originalContracts) return;

    const filtered = this.utils.filterArray(this.originalContracts, this.searchTerm, []);

    this.totalNumberOfItems = filtered.length;

    this.contractSummarySubject.next(filtered);
  }

  onExportExcel() {
    this.isLoading = true;

    this.subscriptions.push(this.service.exportExcel(this.filter, this.translate.getDefaultLang())
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (blob) => {
          saveAs(blob, `${this.translate.instant('Contracts.Title')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`);
        },
        error: () => {
          this.messageToUi.genericError();
        }
      }));
  }

  onToggleSelectContract(isSelected: boolean) {
    const step = isSelected ? 1 : -1;
    this.numberOfSelectedContracts.set(this.numberOfSelectedContracts() + step);
  }

  onTriggerEnter(event: MouseEvent, contractStatusId: number): void {

    if (!this.contractStatusHistoryCache.has(contractStatusId)) {
      const request$ = this.service.getContractStatusHistory(contractStatusId).pipe(shareReplay(1));
      this.contractStatusHistoryCache.set(contractStatusId, request$);
      this.hoverContract$ = request$;
    } else {
      this.hoverContract$ = this.contractStatusHistoryCache.get(contractStatusId)!;
    }
    
    this.popupCounter++;
    this.isMouseOverTrigger.set(true);

    setTimeout(() => {
      const popupEl = document.getElementById('contractStatusHistory');

      if (!popupEl) {
        return;
      }

      const popupHeight = popupEl.clientHeight;

      this.position.x = event.clientX + 10;
      this.position.y = event.clientY - popupHeight - 10;
          this.#changeDetectorRef.detectChanges();
      }, 0); 
  }

  onTriggerLeave(): void {
    this.popupCounter--;

    setTimeout(() => {
    if(this.popupCounter != 0) return;

    this.isMouseOverTrigger.set(false);
    }, 200); ;
  }

  onPopupEnter(): void {
    this.isMouseOverPopup.set(true);
  }

  onPopupLeave(): void {
    this.isMouseOverPopup.set(false);
  }

  getPopupStyle(): { [key: string]: string } {
    const shouldShow = this.isMouseOverTrigger() || this.isMouseOverPopup();

    return {
      display: shouldShow ? 'block' : 'none',
        top: `${this.position.y}px`,
        left: `${this.position.x}px`,
    };
  }

  selectAll(event) {
    const checked = event.target.checked;

    if (!this.originalContracts) return;

    this.originalContracts.forEach(p => {
      p.selected = checked;
    });

    this.contractSummarySubject.next(this.originalContracts);

    const numberOfSelectedContracts = checked ? this.totalNumberOfItems : 0;
    this.numberOfSelectedContracts.set(numberOfSelectedContracts);
  }

  openChangeStatus() {
    let selectedIds: number[];
    let contractsStatusInfo: IUpdatedContractsMultipleStatuses[];
    let uniqueSelectedClaimTypes: string[];
    let isAnyContractWarned: boolean;
    this.subscriptions.push(this.originalList$.subscribe(data => {
      const result = data.filter(p => p.selected == true);
      if (result.length > 0) {
        selectedIds = result.map(x => x.id);
        isAnyContractWarned = result.some(x => x.warnedTotalCapital != 0 || x.warnedTotalFees != 0 || x.warnedTotalRest != 0);
        const selectedClaimTypes = result.map(x => x.claimTypeCode);
        uniqueSelectedClaimTypes = selectedClaimTypes.filter((x, i) => selectedClaimTypes.indexOf(x) === i);
        contractsStatusInfo = result.map(x => ({
          contractStatusId: x.id,
          claimTypeCode: x.claimTypeCode
        } as IUpdatedContractsMultipleStatuses));
      }
      else { this.messageToUi.errorKey('Contracts.NoContractSelected'); }
    }));

    if (!selectedIds || selectedIds.length == 0) { return; }

    const dialogRef = this.matDialog.open(ChangeStatusComponent);
    dialogRef.componentInstance.selectedIds = selectedIds;
    dialogRef.componentInstance.selectedClaimTypeCodes = uniqueSelectedClaimTypes;
    dialogRef.componentInstance.isEnglish = this.isEnglish();
    dialogRef.componentInstance.isAnyContractWarned.set(isAnyContractWarned);

    const claimTypes = this.claimTypes.filter(p => uniqueSelectedClaimTypes.includes(p.claimTypeCode));
    claimTypes.forEach(p => p.selectedStatus = p.statuses.find(x => x.name = 'Open'));
    dialogRef.componentInstance.selectedClaimTypes = claimTypes;
    dialogRef.componentInstance.contractsStatusInfo = contractsStatusInfo;

    this.subscriptions.push(dialogRef.afterClosed().subscribe((result) => { if (result) { this.onSubmit(); } }));
  }

  showCases(contractStatusId: number, contractReference: string) {
    const dialogRef = this.matDialog.open(CasesComponent);
    dialogRef.componentInstance.contractStatusId = contractStatusId;
    dialogRef.componentInstance.contractReference = contractReference;
    dialogRef.afterClosed().subscribe(() => { });
  }

  showExternalStatus(event: any, contractStatusId: number) {
    this.externalStatusData$ = this.service.getExternalStatus(contractStatusId);

    if(this.externalStatus.overlayVisible)
    {
      this.externalStatus.onHide.pipe(take(1),delay(10),tap(() => {
        this.externalStatus.show(event)
      })).subscribe();
      this.externalStatus.hide();
    }
    else
    {this.externalStatus.show(event)}
    
  }

  getClassForExternalStatus(lastUpdateWasSuccessfull: boolean) {
    return lastUpdateWasSuccessfull == true ? '' : 'red';
  }

  showNotes(contractStatusId: number, contractReference: string, contractClosingTypeId: number) {
    const dialogRef = this.matDialog.open(NotesComponent, { id: "notesPopup" });
    dialogRef.componentInstance.contractStatusId = contractStatusId;
    dialogRef.componentInstance.contractReference = contractReference;
    dialogRef.componentInstance.contractClosingTypeId = contractClosingTypeId;
    dialogRef.afterClosed().subscribe((closingTypeId) => {
      if (closingTypeId) {
        this.contractClosingTypeChanges$.next({ contractStatusId, closingTypeId } as IChangedContract);
      }
    });
  }

  getHistoryTooltip(contract: IContractSummary) {
    if (contract.statusChangeHistoryCounts == null) { return 'No history'; }

    return contract.statusChangeHistoryCounts.split('\\').map((value, index) => {
      const label = this.getStatusLabel(contract.claimTypeId, index);

      return label + ' = ' + value;
    }).join('\n');
  }

  getStatusLabel(claimTypeId: number, index: number) {
    const claimType = this.claimTypes.find(x => x.claimTypeId == claimTypeId);
    const status = claimType?.statuses.filter(x => x.includeInHistory == true)[index];

    if (!status) { return ''; }

    return this.isEnglish() ? status.translationEn : status.translation;
  }

  private getNumericScore(score: string): number {
    switch (score) {
    case undefined:
      return -2;
    case 'NR':
      return -1;
    default:
      return Number(score);
    }
  }
}
